import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    AppWindow,
    CheckCircle2,
    Laptop,
    Server,
    HardDrive,
    Cloud,
    FileCheck,
    RotateCcw,
    FileBarChart,
} from "lucide-react";
import DemoRequestModal from "@/components/DemoRequestModal";
import {
    DOWNLOAD_HREF,
    TopBar,
    FooterBar,
    SectionHeader,
    WindowsChrome,
} from "@/components/marketing/Chrome";

/**
 * HowItWorks — /how-it-works. Expands the home page's compact 3-step
 * summary into its own page, the same way Capabilities expanded the
 * landing page's capabilities grid. Reuses the same 3 step mockups built
 * for the home page (kept in sync manually since they're small and
 * page-specific) and brings back the glowing hub-and-spoke flow diagram
 * that was cut from Home during its own rebuild -- still accurate, still
 * built, no reason to let that work go unused.
 */
export default function HowItWorks() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const openContact = () => setContactOpen(true);

    return (
        <div className="relative min-h-screen overflow-x-hidden bg-[#050e22] text-white" data-testid="howitworks-page">
            <div className="pointer-events-none fixed inset-0 grid-bg opacity-[0.1]" />

            <TopBar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} openContact={openContact} />
            <Hero />
            <StepDetails />
            <FlowDiagram />
            <TrustStrip />
            <FinalCTA />
            <FooterBar openContact={openContact} />
            <DemoRequestModal open={contactOpen} onClose={() => setContactOpen(false)} />
        </div>
    );
}

function Hero() {
    return (
        <section className="relative z-10 overflow-hidden border-b border-white/5 px-6 pb-16 pt-14 sm:px-6 sm:pt-16" data-testid="howitworks-hero">
            <div className="pointer-events-none absolute -right-40 top-0 h-[480px] w-[640px] rounded-full bg-[#0066ff]/25 blur-[140px]" />
            <div className="relative mx-auto max-w-3xl text-center">
                <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#3fb8ff]">How It Works</div>
                <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl">
                    Migration without unnecessary <span className="text-[#3fb8ff]">complexity.</span>
                </h1>
                <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-white/65">
                    Three steps. No wasted time. Here's exactly what happens from install to a verified, completed migration.
                </p>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <Link
                        to={DOWNLOAD_HREF}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-6 py-3.5 text-[14px] font-semibold text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110 sm:w-auto"
                    >
                        <AppWindow size={16} />
                        Download for Windows
                    </Link>
                    <Link
                        to="/documentation"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/[0.03] px-6 py-3.5 text-[14px] font-semibold text-white/85 transition hover:border-white/30 hover:bg-white/[0.06] hover:text-white sm:w-auto"
                    >
                        View Documentation
                    </Link>
                </div>
            </div>
        </section>
    );
}

function StepDetails() {
    const STEPS = [
        {
            n: "01", title: "Select Source and Destination",
            body: "Choose where your data currently lives — a PC, a Windows server, a NAS or network share, or a supported cloud destination — and where it needs to go. The same picker works for every migration kind, so switching between a PC-to-PC refresh and a server-to-cloud move doesn't mean learning a different tool.",
            mockup: <MockupSelectSource />,
        },
        {
            n: "02", title: "Configure and Start",
            body: "Set verification, compression, bandwidth caps, and duplicate-file handling before you start — or just go with the defaults. Once started, MoovBytes streams live progress: files transferred, transfer speed, elapsed time, and a running activity log of exactly what's happening.",
            mockup: <MockupConfigure />,
        },
        {
            n: "03", title: "Verify and Complete",
            body: "Every migration finishes with SHA-256 verification against the source, not just a byte count that looks right. When it's done, you get a full report — total data, files transferred, elapsed time, verification result — and can kick off the next migration straight from the same screen.",
            mockup: <MockupMigrateComplete />,
        },
    ];
    return (
        <section className="relative z-10 px-6 py-20 sm:px-6" data-testid="howitworks-steps">
            <div className="mx-auto max-w-[1600px] divide-y divide-white/5">
                {STEPS.map((s, i) => (
                    <div key={s.n} className={`grid items-center gap-10 py-14 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                        <div>
                            <div className="font-mono text-[11px] font-bold uppercase tracking-[0.28em] text-[#3fb8ff]">Step {s.n}</div>
                            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">{s.title}</h2>
                            <p className="mt-4 max-w-lg text-[14px] leading-relaxed text-white/65">{s.body}</p>
                        </div>
                        <div className="mx-auto w-full max-w-md">
                            <WindowsChrome title="MoovBytes">{s.mockup}</WindowsChrome>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function MockupSelectSource() {
    const OPTIONS = [
        ["PC to PC", "Transfer between two Windows PCs", Laptop],
        ["Server to Server", "Migrate between Windows servers", Server],
        ["NAS to NAS", "Transfer from/to network storage", HardDrive],
        ["Cloud Migration", "Move data to or from cloud storage", Cloud],
    ];
    return (
        <div className="space-y-2.5">
            <div className="text-[12px] font-bold text-white">New Migration</div>
            <div className="text-[9px] text-white/40">Select Migration Type</div>
            {OPTIONS.map(([label, desc, Icon]) => (
                <div key={label} className={`flex items-center gap-2.5 rounded-md border p-2.5 ${label === "PC to PC" ? "border-[#00C2FF]/50 bg-[#00C2FF]/10" : "border-white/10 bg-white/[0.02]"}`}>
                    <Icon size={15} className={label === "PC to PC" ? "text-[#3fb8ff]" : "text-white/45"} />
                    <div className="min-w-0">
                        <div className="truncate text-[10px] font-semibold text-white/85">{label}</div>
                        <div className="truncate text-[8px] text-white/40">{desc}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function MockupConfigure() {
    return (
        <div className="space-y-2.5">
            <div className="text-[12px] font-bold text-white">Migration in Progress</div>
            <div className="text-[9px] text-white/40">Transferring data from WIN-PC-01 to FS-01</div>
            <div className="flex items-center gap-2">
                <div className="h-1.5 flex-1 rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#5b5ef0] to-[#00C2FF]" style={{ width: "62%" }} />
                </div>
                <span className="text-[10px] font-bold tabular-nums text-white/85">62%</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
                {[["Files Transferred", "412,880"], ["Transfer Speed", "612 MB/s"], ["Elapsed Time", "28m"], ["Status", "Transferring"]].map(([k, v]) => (
                    <div key={k} className="rounded border border-white/10 bg-white/[0.02] p-2">
                        <div className="text-[7px] uppercase tracking-wide text-white/40">{k}</div>
                        <div className={`mt-0.5 text-[9.5px] font-semibold ${k === "Status" ? "text-[#3fb8ff]" : "text-white/85"}`}>{v}</div>
                    </div>
                ))}
            </div>
            <div className="rounded border border-white/10 bg-white/[0.02] p-2">
                {[["Copying user data", true], ["Transferring application data", false]].map(([label, done]) => (
                    <div key={label} className="flex items-center gap-1.5 py-0.5 text-[8.5px] text-white/60">
                        <CheckCircle2 size={10} className={done ? "text-emerald-400" : "text-white/25"} />
                        {label}
                    </div>
                ))}
            </div>
        </div>
    );
}

function MockupMigrateComplete() {
    return (
        <div className="space-y-2.5 text-center">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/15">
                <CheckCircle2 size={22} className="text-emerald-400" />
            </div>
            <div className="text-[12px] font-bold text-white">Migration Completed Successfully</div>
            <div className="text-[9px] text-white/40">Your data has been transferred.</div>
            <div className="grid grid-cols-2 gap-2 text-left">
                {[["Total Data", "1.87 TB"], ["Files Transferred", "1,582,941"], ["Elapsed Time", "2h 14m"], ["Verification", "Passed"]].map(([k, v]) => (
                    <div key={k} className="rounded border border-white/10 bg-white/[0.02] p-2">
                        <div className="text-[7px] uppercase tracking-wide text-white/40">{k}</div>
                        <div className={`mt-0.5 text-[9.5px] font-semibold ${k === "Verification" ? "text-emerald-400" : "text-white/85"}`}>{v}</div>
                    </div>
                ))}
            </div>
            <div className="flex gap-2">
                <button type="button" className="flex-1 rounded border border-white/10 py-1.5 text-[8.5px] font-medium text-white/70">View Report</button>
                <button type="button" className="flex-1 rounded bg-[#00C2FF] py-1.5 text-[8.5px] font-semibold text-[#03122b]">Start New Migration</button>
            </div>
        </div>
    );
}

function FlowDiagram() {
    const NODES = [
        { label: "Windows PC", icon: Laptop },
        { label: "Windows Server", icon: Server },
        { label: "NAS / Network Share", icon: HardDrive },
        { label: "Cloud Storage", icon: Cloud },
    ];
    const NODE_Y = [12.5, 37.5, 62.5, 87.5];
    const HUB_EDGE_Y = [38, 45, 55, 62];
    const BEND_X_L = [36, 38, 40, 42];
    const BEND_X_R = [64, 62, 60, 58];

    return (
        <section className="relative z-10 border-t border-white/5 bg-white/[0.015] px-6 py-20 sm:px-6" data-testid="howitworks-flow">
            <div className="mx-auto max-w-[1600px]">
                <SectionHeader eyebrow="One workflow" title="A single solution for any migration path." />
                <div className="mx-auto mt-12 max-w-4xl rounded-2xl border border-white/10 bg-white/[0.03] p-8">
                    <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-10">
                        <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                            {NODE_Y.map((y, i) => (
                                <path key={`glow-l-${y}`} d={`M 30 ${y} C ${BEND_X_L[i]} ${y}, ${BEND_X_L[i]} ${HUB_EDGE_Y[i]}, 45 ${HUB_EDGE_Y[i]}`} fill="none" stroke="#00C2FF" strokeWidth="2.5" strokeOpacity="0.3" vectorEffect="non-scaling-stroke" style={{ filter: "blur(2px)" }} />
                            ))}
                            {NODE_Y.map((y, i) => (
                                <path key={`line-l-${y}`} d={`M 30 ${y} C ${BEND_X_L[i]} ${y}, ${BEND_X_L[i]} ${HUB_EDGE_Y[i]}, 45 ${HUB_EDGE_Y[i]}`} fill="none" stroke="#00C2FF" strokeWidth="1" strokeOpacity="0.8" vectorEffect="non-scaling-stroke" />
                            ))}
                            {NODE_Y.map((y, i) => (
                                <path key={`glow-r-${y}`} d={`M 55 ${HUB_EDGE_Y[i]} C ${BEND_X_R[i]} ${HUB_EDGE_Y[i]}, ${BEND_X_R[i]} ${y}, 70 ${y}`} fill="none" stroke="#00C2FF" strokeWidth="2.5" strokeOpacity="0.3" vectorEffect="non-scaling-stroke" style={{ filter: "blur(2px)" }} />
                            ))}
                            {NODE_Y.map((y, i) => (
                                <path key={`line-r-${y}`} d={`M 55 ${HUB_EDGE_Y[i]} C ${BEND_X_R[i]} ${HUB_EDGE_Y[i]}, ${BEND_X_R[i]} ${y}, 70 ${y}`} fill="none" stroke="#00C2FF" strokeWidth="1" strokeOpacity="0.8" vectorEffect="non-scaling-stroke" />
                            ))}
                        </svg>

                        <div className="relative space-y-3">
                            {NODES.map((n) => (
                                <div key={n.label} className="flex items-center gap-3 rounded-lg border border-white/10 bg-[#050e22] px-4 py-3 text-[13px] text-white/75 shadow-[0_0_14px_-6px_rgba(0,194,255,0.6)]">
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#00C2FF]/40 bg-[#00C2FF]/15 shadow-[0_0_18px_3px_rgba(0,194,255,0.55)]">
                                        <n.icon size={16} className="text-[#3fb8ff] drop-shadow-[0_0_6px_rgba(63,184,255,1)]" />
                                    </span>
                                    <span className="truncate">{n.label}</span>
                                </div>
                            ))}
                        </div>

                        <div className="relative flex flex-col items-center gap-2.5 px-3">
                            <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-[#00C2FF]/40 bg-[#0a1a33] shadow-[0_0_30px_-4px_rgba(0,194,255,0.5)]">
                                <img src="/brand/moovbytes-icon.png" alt="" className="h-12 w-12 rounded-md" />
                            </div>
                            <div className="text-center text-[12px] font-semibold text-white/80">MoovBytes</div>
                            <div className="text-center text-[10.5px] leading-tight text-white/45">Secure Migration<br />Verified Transfers</div>
                        </div>

                        <div className="relative space-y-3">
                            {NODES.map((n) => (
                                <div key={n.label} className="flex items-center gap-3 rounded-lg border border-white/10 bg-[#050e22] px-4 py-3 text-[13px] text-white/75 shadow-[0_0_14px_-6px_rgba(0,194,255,0.6)]">
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#00C2FF]/40 bg-[#00C2FF]/15 shadow-[0_0_18px_3px_rgba(0,194,255,0.55)]">
                                        <n.icon size={16} className="text-[#3fb8ff] drop-shadow-[0_0_6px_rgba(63,184,255,1)]" />
                                    </span>
                                    <span className="truncate">{n.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function TrustStrip() {
    const POINTS = [
        { icon: FileCheck, title: "SHA-256 verification", body: "Every migration is verified block by block, not just counted as done." },
        { icon: RotateCcw, title: "Resumable by default", body: "An interrupted transfer picks up where it stopped — no restart." },
        { icon: FileBarChart, title: "A report every time", body: "What moved, what was verified, and what needs a second look." },
    ];
    return (
        <section className="relative z-10 px-6 py-16 sm:px-6" data-testid="howitworks-trust">
            <div className="mx-auto grid max-w-[1600px] gap-6 sm:grid-cols-3">
                {POINTS.map((p) => (
                    <div key={p.title} className="rounded-xl border border-white/10 bg-white/[0.03] p-6">
                        <p.icon size={20} className="text-[#3fb8ff]" />
                        <div className="mt-3 text-[14px] font-bold text-white">{p.title}</div>
                        <p className="mt-1.5 text-[12.5px] leading-relaxed text-white/60">{p.body}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

function FinalCTA() {
    return (
        <section className="relative z-10 px-6 pb-20 sm:px-6" data-testid="howitworks-cta">
            <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-6 rounded-2xl border border-white/10 bg-gradient-to-br from-[#00C2FF]/10 via-white/[0.03] to-[#5b5ef0]/15 p-8 sm:flex-row sm:items-center">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">See it for yourself</div>
                    <h2 className="mt-2 font-display text-xl font-bold tracking-tight sm:text-2xl">Try it on your first migration.</h2>
                    <p className="mt-1.5 text-[13px] text-white/60">Download MoovBytes and see the three steps run for real.</p>
                </div>
                <div className="shrink-0 text-center">
                    <Link
                        to={DOWNLOAD_HREF}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110"
                    >
                        <AppWindow size={16} />
                        Download for Windows
                    </Link>
                    <div className="mt-2 text-[11px] text-white/40">Windows 10/11 · Windows Server 2019–2025 · 64-bit</div>
                </div>
            </div>
        </section>
    );
}
